import type { Customer, CustomerDocumentType } from "@fiscalo/shared-types";

import { apiClient } from "@/services/apiClient";

export interface Client extends Customer {
  createdAt?: string;
  updatedAt?: string;
  /**
   * Default marca for this client's invoices; null = none.
   *
   * On the portal's Client rather than on the shared Customer type: the
   * marca is the ISSUER's identity, and Customer describes the receptor.
   */
  defaultBrandId?: string | null;
}

export type { CustomerDocumentType };

export interface ListClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  documentType?: "RUT" | "CI" | "EXT";
  defaultCurrency?: "USD" | "UYU";
}

export interface ListClientsResponse {
  data: Client[];
  total: number;
}

export type InvoiceEmailTemplate = "default" | "minimal" | "custom";

export interface CreateClientPayload {
  legalName: string;
  displayName?: string;
  documentType: CustomerDocumentType;
  rut?: string;
  countryCode?: string;
  docRecepExt?: string;
  address?: string;
  ciudad?: string;
  departamento?: string;
  email?: string;
  phone?: string;
  defaultCurrency?: "USD" | "UYU";
  paymentTermDays?: 0 | 30 | 60 | 90;
  discountPercent?: number;
  sendInvoiceByEmail?: boolean;
  autoSendInvoiceEmail?: boolean;
  invoiceEmail?: string | null;
  invoiceEmailCc?: string | null;
  invoiceEmailTemplate?: InvoiceEmailTemplate;
  invoiceEmailCustomBody?: string;
  invoiceEmailCustomSubject?: string;
  invoiceEmailIncludeCompanyLogo?: boolean;
  invoiceEmailIncludeInvoiceDetails?: boolean;
  pdfFilenameTemplate?: string;
  language?: "es" | "en";
  /**
   * Default marca for this client's invoices. Null clears it. Applied only
   * when the invoice names no brand and inherits none.
   */
  defaultBrandId?: string | null;
}

export interface UpdateClientPayload {
  legalName?: string;
  displayName?: string;
  documentType?: CustomerDocumentType;
  rut?: string;
  countryCode?: string;
  docRecepExt?: string;
  address?: string;
  ciudad?: string;
  departamento?: string;
  /** null explicitly clears the stored contact email. */
  email?: string | null;
  phone?: string;
  defaultCurrency?: "USD" | "UYU";
  paymentTermDays?: 0 | 30 | 60 | 90;
  discountPercent?: number;
  sendInvoiceByEmail?: boolean;
  autoSendInvoiceEmail?: boolean;
  invoiceEmail?: string | null;
  invoiceEmailCc?: string | null;
  invoiceEmailTemplate?: InvoiceEmailTemplate;
  invoiceEmailCustomBody?: string | null;
  invoiceEmailCustomSubject?: string | null;
  invoiceEmailIncludeCompanyLogo?: boolean;
  invoiceEmailIncludeInvoiceDetails?: boolean;
  pdfFilenameTemplate?: string | null;
  language?: "es" | "en";
  /**
   * Default marca for this client's invoices. Null clears it. Applied only
   * when the invoice names no brand and inherits none.
   */
  defaultBrandId?: string | null;
}

export interface RucInfoForClientResponse {
  valid: boolean;
  legalName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
}

export function getRucInfoForClient(rut: string): Promise<RucInfoForClientResponse> {
  return apiClient
    .get<RucInfoForClientResponse>("/clients/ruc-info", { params: { rut } })
    .then((res) => res.data);
}

const LIST_CLIENTS_SELECT_PAGE_SIZE = 100;
const LIST_CLIENTS_SELECT_MAX_PAGES = 50;

/**
 * Loads all clients for pickers (e.g. recurring invoice). The API caps `limit` at 100; this
 * follows pages until every row is fetched or a safety cap is reached.
 */
export async function listClientsAllForSelection(): Promise<Client[]> {
  const all: Client[] = [];
  let page = 1;
  while (page <= LIST_CLIENTS_SELECT_MAX_PAGES) {
    const { data, total } = await listClients({
      page,
      limit: LIST_CLIENTS_SELECT_PAGE_SIZE,
    });
    all.push(...data);
    if (
      data.length < LIST_CLIENTS_SELECT_PAGE_SIZE ||
      all.length >= total
    ) {
      break;
    }
    page += 1;
  }
  return all;
}

export function listClients(
  params: ListClientsParams = {}
): Promise<ListClientsResponse> {
  const { page = 1, limit = 10, search, documentType, defaultCurrency } =
    params;
  const requestParams: Record<string, string | number> = { page, limit };
  if (search?.trim()) requestParams.search = search.trim();
  if (documentType) requestParams.documentType = documentType;
  if (defaultCurrency) requestParams.defaultCurrency = defaultCurrency;
  return apiClient
    .get<ListClientsResponse>("/clients", {
      params: requestParams,
    })
    .then((res) => res.data);
}

export function getClient(id: string): Promise<Client> {
  return apiClient.get<Client>(`/clients/${id}`).then((res) => res.data);
}

export function createClient(payload: CreateClientPayload): Promise<Client> {
  return apiClient.post<Client>("/clients", payload).then((res) => res.data);
}

export function updateClient(
  id: string,
  payload: UpdateClientPayload
): Promise<Client> {
  return apiClient
    .patch<Client>(`/clients/${id}`, payload)
    .then((res) => res.data);
}

export function deleteClient(id: string): Promise<void> {
  return apiClient.delete(`/clients/${id}`).then(() => undefined);
}

export interface PreviewClientInvoiceEmailPayload {
  invoiceEmailTemplate: InvoiceEmailTemplate;
  invoiceEmailCustomBody?: string;
  invoiceEmailIncludeCompanyLogo?: boolean;
  invoiceEmailIncludeInvoiceDetails?: boolean;
}

export function previewClientInvoiceEmail(
  payload: PreviewClientInvoiceEmailPayload
): Promise<{ html: string }> {
  return apiClient
    .post<{ html: string }>("/clients/preview-invoice-email", payload)
    .then((res) => res.data);
}
